import { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { ApiError, auth, candidateApi } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { AuthCard } from '../components/auth/AuthCard';
import { ErrorBanner } from '../components/auth/ErrorBanner';

interface LocationState {
  deleted_at?: string;
  days_in_soft_delete?: number;
}

/**
 * /recover-account — landing page for the soft-delete recovery flow (analysis §5.8 +
 * Phase 7-dashboard-a §5). Reachable only after `auth.signin` returned the
 * `ACCOUNT_DELETED_RECOVERABLE` envelope (SignInPage navigates here with the metadata
 * via `location.state`). The recovery cookie has already been minted server-side and
 * is path-scoped to `/api/v1/candidates/me/recover` — clicking Restore consumes it.
 *
 * If the candidate hits this page directly without a recovery cookie (deep link,
 * stale session) the recover endpoint returns AUTH_INVALID — we surface that as a
 * friendly "Please sign in again" prompt rather than a raw error.
 */
export function RecoverAccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setCandidate } = useAuth();
  const state = (location.state ?? {}) as LocationState;

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRestore = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const me = await candidateApi.account.recover();
      setCandidate(me);
      navigate('/dashboard/profile', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.code === 'AUTH_INVALID'
            ? 'Your recovery link expired. Please sign in again to restart.'
            : err.message,
        );
      } else {
        setError('Could not restore your account. Please sign in again.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [navigate, setCandidate]);

  const handleSignOut = useCallback(async () => {
    try {
      await auth.signout();
    } catch {
      // best-effort; the recovery cookie is short-lived and not exposed to other endpoints.
    }
    navigate('/', { replace: true });
  }, [navigate]);

  return (
    <AuthCard
      publicTheme
      eyebrow="Account recovery"
      title="Welcome back — your account is deleted"
      subtitle="You can restore your profile, applications, and resumes with one click."
      footer={
        <>
          Changed your mind?{' '}
          <Link
            to="/"
            className="text-brand-primary font-bold hover:text-brand-accent transition-colors"
          >
            Back to home
          </Link>
        </>
      }
    >
      <ErrorBanner message={error} />

      {state.deleted_at && (
        <p className="text-sm text-brand-muted mb-5">
          You deleted your account on{' '}
          <span className="font-bold text-brand-primary">{formatDate(state.deleted_at)}</span>
          {typeof state.days_in_soft_delete === 'number' && (
            <>
              {' '}
              ({state.days_in_soft_delete} {state.days_in_soft_delete === 1 ? 'day' : 'days'} ago).
            </>
          )}
        </p>
      )}

      <div className="rounded-2xl bg-brand-secondary/30 border border-brand-accent/20 p-4 mb-5 flex gap-3">
        <ShieldCheck className="w-5 h-5 text-brand-deep shrink-0 mt-0.5" />
        <p className="text-sm text-brand-deep">
          Your profile, applications, and resumes are still safe and will be fully restored if you
          choose to recover.
        </p>
      </div>

      <button
        type="button"
        onClick={() => void handleRestore()}
        disabled={submitting}
        className="w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-brand-deep transition-all shadow-lg shadow-brand-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Restoring…
          </>
        ) : (
          <>
            Restore my account
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>

      <button
        type="button"
        onClick={() => void handleSignOut()}
        disabled={submitting}
        className="w-full mt-3 text-xs font-black uppercase tracking-[0.2em] text-brand-muted hover:text-brand-primary transition-colors disabled:opacity-50"
      >
        I don't want to recover — Sign out
      </button>
    </AuthCard>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}
